import type { CustomFormField, CustomerConsent } from '../../types/account';
import type { Config, Content } from '../../types/config';
import type { GenericFormValues } from '../../types/form';
import type { Playlist, PlaylistItem } from '../../types/playlist';
import { CARD_ASPECT_RATIOS } from '../constants';

export type PosterAspectRatio = (typeof CARD_ASPECT_RATIOS)[number];

const getFiltersFromConfig = (config: Config, playlistId: string | undefined): string[] => {
  const menuItem = config.menu.find((item) => item.contentId === playlistId);
  const filters = menuItem?.filterTags?.split(',').filter(Boolean);

  return filters || [];
};

const filterPlaylist = (playlist: Playlist, filter: string) => {
  if (filter === '') return playlist;

  return {
    ...playlist,
    playlist: playlist.playlist.filter(({ tags }) => (tags ? tags.split(',').includes(filter) : false)),
  };
};

const chunk = <T>(input: T[], size: number) => {
  return input?.reduce((arr: T[][], item, idx: number) => {
    return idx % size === 0 ? [...arr, [item]] : [...arr.slice(0, -1), [...arr.slice(-1)[0], item]];
  }, []);
};

const findPlaylistImageForWidth = (playlistItem: PlaylistItem, width: number): string =>
  playlistItem.images.find((img) => img.width === width)?.src || playlistItem.image;

const generatePlaylistPlaceholder = (playlistLength: number = 15): Playlist => ({
  title: '',
  playlist: new Array(playlistLength).fill({}).map(
    (_value, index) =>
      ({
        description: '',
        duration: 0,
        feedid: '',
        image: '',
        images: [],
        cardImage: '',
        backgroundImage: '',
        channelLogoImage: '',
        link: '',
        genre: '',
        mediaid: `placeholder_${index}`,
        pubdate: 0,
        rating: '',
        sources: [],
        tags: '',
        title: '',
        tracks: [],
      } as PlaylistItem),
  ),
});

const formatConsentValues = (publisherConsents: CustomFormField[] | null = [], customerConsents: CustomerConsent[] | null = []) => {
  if (!publisherConsents || !customerConsents) {
    return {};
  }

  const values = publisherConsents?.reduce((acc, publisherConsent) => {
    const consent = customerConsents?.find((customerConsent) => customerConsent.name === publisherConsent.name);

    if (consent) {
      const value = publisherConsent.isCustomRegisterField ? consent.value ?? '' : consent.state === 'accepted';
      acc[publisherConsent.name] = value;
    }

    return acc;
  }, {} as Record<string, string | boolean>);

  return values;
};

const formatConsents = (publisherConsents: CustomFormField[] | null = [], customerConsents: CustomerConsent[] | null = []) => {
  if (!publisherConsents || !customerConsents) {
    return {};
  }
  const values: Record<string, boolean> = {};
  publisherConsents?.forEach((publisherConsent) => {
    if (customerConsents?.find((customerConsent) => customerConsent.name === publisherConsent.name && customerConsent.state === 'accepted')) {
      values[publisherConsent.name] = true;
    }
  });

  return values;
};

const extractConsentValues = (consents?: CustomFormField[]) => {
  const values: Record<string, string | boolean> = {};

  if (!consents) {
    return values;
  }

  consents?.forEach((consent) => {
    values[consent.name] = consent.type === 'checkbox' ? consent.enabledByDefault === true : consent.defaultValue ?? '';
  });

  return values;
};

const formatConsentsFromValues = (publisherConsents: CustomFormField[] | null, values?: GenericFormValues) => {
  const consents: CustomerConsent[] = [];

  if (!publisherConsents || !values) return consents;

  publisherConsents.forEach((consent) => {
    consents.push({
      name: consent.name,
      version: consent.version,
      state: values[consent.name] ? 'accepted' : 'declined',
      value: values[consent.name] ?? '',
    });
  });

  return consents;
};

const checkConsentsFromValues = (publisherConsents: CustomFormField[], consents: Record<string, string | boolean>) => {
  const customerConsents: CustomerConsent[] = [];
  const consentsErrors: string[] = [];

  if (!publisherConsents || !consents) return { customerConsents, consentsErrors };

  publisherConsents.forEach((consent) => {
    if (consent.required && !consents[consent.name]) {
      consentsErrors.push(consent.name);
    }

    customerConsents.push({
      name: consent.name,
      version: consent.version,
      state: consents[consent.name] ? 'accepted' : 'declined',
      value: consents[consent.name] ?? '',
    });
  });

  return { customerConsents, consentsErrors };
};

const isNotEmptyConsent = (consent: CustomerConsent) => consent.value !== '';

const formatConsentToRegisterField = ({ name, value = '' }: CustomerConsent, _: number, collection: CustomerConsent[]) => {
  const val = (() => {
    if (name === 'us_state') {
      if (collection.find(({ name, value }) => name === 'country' && value === 'us')) {
        return value === 'n/a' ? '' : value;
      }

      return 'n/a';
    }

    const isBoolean = value === true || value === false;

    if (isBoolean && name !== 'terms') {
      return value ? 'on' : 'off';
    }

    return value;
  })();

  return [name, val] as const;
};

const formatConsentsToRegisterFields = (consents: CustomerConsent[]) =>
  Object.fromEntries(consents.filter(isNotEmptyConsent).map(formatConsentToRegisterField));

const deepCopy = (obj: unknown) => {
  if (Array.isArray(obj) || (typeof obj === 'object' && obj !== null)) {
    return JSON.parse(JSON.stringify(obj));
  }
  return obj;
};

const parseAspectRatio = (input: unknown) => {
  if (typeof input === 'string' && (CARD_ASPECT_RATIOS as readonly string[]).includes(input)) return input as PosterAspectRatio;
};

const parseTilesDelta = (posterAspect?: string, portraitDelta = 1) => {
  if (!posterAspect) return 0;

  const parts = posterAspect.split(':');
  if (parts.length !== 2) return 0;

  const width = parseInt(parts[0]);
  const height = parseInt(parts[1]);

  // show X more cards when portrait
  return height > width ? portraitDelta : 0;
};

const parsePlaylistIds = (input: unknown): Content[] => {
  const playlistIds = typeof input === 'string' ? input.replace(/\s+/g, '').split(',') : [];

  return playlistIds.map((id) => ({ type: 'playlist', contentId: id }));
};

/**
 * Removes duplicate items from an array based on a custom key function.
 * Only the first occurrence of each unique key is kept.
 */
function deduplicateBy<T>(array: T[], keyFn: (item: T) => string): T[] {
  const seen = new Set<string>();
  return array.filter((item) => {
    const key = keyFn(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}
const canFeedBeUsedAsMediaPlaylist = (config: Config, feedId: string | null) => {
  if (!feedId) return false;

  return ![config.features?.searchPlaylist, config.features?.favoritesList, config.features?.continueWatchingList].includes(feedId);
};

export {
  getFiltersFromConfig,
  filterPlaylist,
  chunk,
  findPlaylistImageForWidth,
  generatePlaylistPlaceholder,
  formatConsentValues,
  formatConsents,
  formatConsentsFromValues,
  parsePlaylistIds,
  extractConsentValues,
  checkConsentsFromValues,
  deepCopy,
  parseAspectRatio,
  parseTilesDelta,
  isNotEmptyConsent,
  formatConsentToRegisterField,
  formatConsentsToRegisterFields,
  deduplicateBy,
  canFeedBeUsedAsMediaPlaylist,
};
